/**
 * FiltersManager
 * Handles filters, accordions, active tags, and reset controls
 */
export default class FiltersManager {
    constructor(block, stepsManager, poiManager) {
        this.block = block
        this.stepsManager = stepsManager
        this.poiManager = poiManager
        this.isSidebarOpen = false

        this.CONFIG = {
            FILTER_OPTION: '[data-action="filter-option"]',
            TOGGLE_FILTER: '[data-action="toggle-filter"]',
            FILTER_OPTIONS: '[data-element="filter-options"]',
            SHOW_MORE: '[data-action="show-more"]',
            RESET_ALL: '[data-action="reset-all-filters"]',
            ACTIVE_TAGS: '[data-element="active-tags"]',
            ACTIVE_TAGS_LIST: '[data-element="active-tags-list"]',
            STEP_CARD: "[data-step-card]",
            CLOSE_STEP_CARD: '[data-action="close-step-card"]',
            SIDEBAR: ".customer-block__interactive-map__sidebar",
            SIDEBAR_OVERLAY: '[data-element="sidebar-overlay"]',
            SIDEBAR_TRIGGER: '[data-trigger="sidebar"]',
        }
    }

    /**
     * Initializes all filter handlers
     */
    initialize() {
        this.initAccordions()
        this.initShowMore()
        this.initResetButtons()
        this.initMapFilters()
        this.initStepDeselection()
        this.initSidebarToggle()
        this.setupEventListeners()
        this.applyURLFilters()
        this.applyDefaultFilters()
    }

    // ==========================================
    // EVENT LISTENERS
    // ==========================================

    applyURLFilters() {
        const urlParams = new URLSearchParams(window.location.search)
        // 1. ?step=ID query parameter
        const stepId = urlParams.get("step")
        if (stepId) {
            this.triggerFilter(stepId)
        }
        // 2. ?poi=cat1,cat2 query parameter
        const poiParam = urlParams.get("poi")
        if (poiParam) {
            const poiCategories = poiParam.split(",")
            for (let i = 0; i < poiCategories.length; i++) {
                this.triggerFilter(poiCategories[i].trim())
            }
        }
    }

    /**
     * Activates default POI filters after the track has loaded
     */
    applyDefaultFilters() {
        this.triggerFilter("incontournable")
    }

    triggerFilter(value) {
        // 1. Find the input by its raw value
        let selector = `${this.CONFIG.FILTER_OPTION}[value="${value}"]`
        let input = this.block.querySelector(selector)
        // 2. If not found, try with the step prefix (e.g. gpx-step-315)
        if (!input && !value.startsWith("gpx-step-")) {
            selector = `${this.CONFIG.FILTER_OPTION}[value="gpx-step-${value}"]`
            input = this.block.querySelector(selector)
        }
        if (input) {
            // Avoid re-triggering when already checked (checkboxes)
            if (input.checked && input.type !== "radio") return
            input.checked = true

            // Run filtering (POI load / track)
            this.filterMap({target: input})
            // UX: open the parent accordion
            const accordion = input.closest(".customer-block__interactive-map__filter-item")
            const toggle = accordion?.querySelector(this.CONFIG.TOGGLE_FILTER)
            if (toggle && !toggle.classList.contains("is-open")) {
                this.toggleAccordion(toggle)
            }
        } else {
            console.warn(`FiltersManager: No filter found for value "${value}"`)
        }
    }

    setupEventListeners() {
        // Listen for step selection events
        this.block.addEventListener("select-step", (event) => {
            const stepId = event.detail.stepId
            const filterOption = this.block.querySelector(`[data-filter-key="steps"][value="${stepId}"]`)

            if (filterOption) {
                filterOption.checked = true
                this.updateActiveTags()
            }
        })

        // Listen for active-tags refresh requests
        this.block.addEventListener("update-active-tags", () => {
            this.updateActiveTags()
        })
    }

    // ==========================================
    // SIDEBAR TOGGLE (Mobile)
    // ==========================================

    initSidebarToggle() {
        const toggleBtns = this.block.querySelectorAll(this.CONFIG.SIDEBAR_TRIGGER)
        const overlay = this.block.querySelector(this.CONFIG.SIDEBAR_OVERLAY)

        if (!toggleBtns) return

        // Toggle on button click
        for (let i = 0; i < toggleBtns.length; i++) {
            const button = toggleBtns[i]
            button.addEventListener("click", () => {
                this.toggleSidebar()
            })
        }

        // Close when clicking the overlay
        overlay?.addEventListener("click", () => {
            this.closeSidebar()
        })
    }

    toggleSidebar() {
        if (this.isSidebarOpen) {
            this.closeSidebar()
        } else {
            this.openSidebar()
        }
    }

    openSidebar() {
        const sidebar = this.block.querySelector(this.CONFIG.SIDEBAR)
        const overlay = this.block.querySelector(this.CONFIG.SIDEBAR_OVERLAY)

        sidebar?.classList.add("is-open")
        overlay?.classList.add("is-visible")
        this.isSidebarOpen = true

        // Prevent body scroll
        document.body.style.overflow = "hidden"
    }

    closeSidebar() {
        const sidebar = this.block.querySelector(this.CONFIG.SIDEBAR)
        const overlay = this.block.querySelector(this.CONFIG.SIDEBAR_OVERLAY)

        sidebar?.classList.remove("is-open")
        overlay?.classList.remove("is-visible")
        this.isSidebarOpen = false

        // Restore body scroll
        document.body.style.overflow = ""
    }

    // ==========================================
    // ACCORDIONS
    // ==========================================

    initAccordions() {
        const toggleButtons = this.block.querySelectorAll(this.CONFIG.TOGGLE_FILTER)

        for (let i = 0; i < toggleButtons.length; i++) {
            const button = toggleButtons[i]
            button.addEventListener("click", (e) => {
                e.preventDefault()
                this.toggleAccordion(button)
            })
        }
    }

    toggleAccordion(button) {
        const filterItem = button.closest(".customer-block__interactive-map__filter-item")
        const options = filterItem.querySelector(this.CONFIG.FILTER_OPTIONS)

        if (!options) return

        const isOpen = button.classList.contains("is-open")

        if (isOpen) {
            button.classList.remove("is-open")
            options.classList.remove("is-open")
        } else {
            button.classList.add("is-open")
            options.classList.add("is-open")
        }
    }

    // ==========================================
    // SHOW MORE
    // ==========================================

    initShowMore() {
        const showMoreButtons = this.block.querySelectorAll(this.CONFIG.SHOW_MORE)

        for (let i = 0; i < showMoreButtons.length; i++) {
            const button = showMoreButtons[i]
            const options = button.closest(this.CONFIG.FILTER_OPTIONS)
            const allOptions = options?.querySelectorAll(".customer-block__interactive-map__filter-option")

            // Hide options beyond the first 5
            if (allOptions && allOptions.length > 5) {
                for (let j = 0; j < allOptions.length; j++) {
                    if (j >= 5) {
                        const option = allOptions[j]
                        option.style.display = "none"
                        option.dataset.hidden = "true"
                    }
                }
            }

            button.addEventListener("click", (e) => {
                e.preventDefault()
                this.toggleShowMore(button, options)
            })
        }
    }

    toggleShowMore(button, options) {
        const hiddenOptions = options?.querySelectorAll('[data-hidden="true"]')
        const isExpanded = button.dataset.expanded === "true"

        if (isExpanded) {
            if (hiddenOptions) {
                for (let i = 0; i < hiddenOptions.length; i++) {
                    hiddenOptions[i].style.display = "none"
                }
            }
            button.textContent = `+ ${IRISCollectionCustomer.a11y.showMore}`
            button.dataset.expanded = "false"
        } else {
            if (hiddenOptions) {
                for (let i = 0; i < hiddenOptions.length; i++) {
                    hiddenOptions[i].style.display = "flex"
                }
            }
            button.textContent = `- ${IRISCollectionCustomer.a11y.showLess}`
            button.dataset.expanded = "true"
        }
    }

    // ==========================================
    // RESET BUTTONS
    // ==========================================

    initResetButtons() {
        const resetButtons = this.block.querySelectorAll(this.CONFIG.RESET_ALL)

        for (let i = 0; i < resetButtons.length; i++) {
            const button = resetButtons[i]
            button.addEventListener("click", (e) => {
                e.preventDefault()
                this.resetAllFilters()
            })
        }
    }

    resetAllFilters() {
        // Uncheck all inputs
        const allInputs = this.block.querySelectorAll(this.CONFIG.FILTER_OPTION)
        for (let i = 0; i < allInputs.length; i++) {
            allInputs[i].checked = false
        }

        // Remove active class from all options
        const allOptions = this.block.querySelectorAll(".customer-block__interactive-map__filter-option")
        for (let i = 0; i < allOptions.length; i++) {
            allOptions[i].classList.remove("is-active")
        }

        // Reset managers
        this.stepsManager?.resetAll()
        this.poiManager?.resetAll()

        // Refresh active tags
        this.updateActiveTags()
    }

    // ==========================================
    // ACTIVE TAGS
    // ==========================================

    updateActiveTags() {
        const container = this.block.querySelector(this.CONFIG.ACTIVE_TAGS)
        const list = this.block.querySelector(this.CONFIG.ACTIVE_TAGS_LIST)

        if (!container || !list) return

        // Clear the list
        list.innerHTML = ""

        const activeStep = this.stepsManager?.getSelectedStep()
        const activePois = this.poiManager?.getActiveTypes()

        const activeFilters = [activeStep, ...activePois].filter((filter) => filter !== null)

        if (activeFilters.length > 0) {
            container.style.display = "block"

            // Build tags
            for (let i = 0; i < activeFilters.length; i++) {
                const data = activeFilters[i]
                if (data && data.id && data.title) {
                    const tag = document.createElement("span")
                    tag.className = "active-tag"
                    tag.innerHTML = `
                    <button type="button" data-remove-filter="${data.id}" aria-label="${IRISCollectionCustomer.a11y.removeFilter}">
                        <i class="fi fi--customer-close"></i>
                    </button>
                    ${data.title}
                `
                    list.appendChild(tag)

                    // Remove-tag click handler
                    tag.querySelector("button").addEventListener("click", () => {
                        this.removeActiveTag(data.id)
                    })
                }
            }
        } else {
            container.style.display = "none"
        }

        // Update the bottom-bar toggle badge count
        this.updateFiltersBadge(activeFilters.length)
    }

    updateFiltersBadge(count) {
        const toggleBtn = this.block.querySelector(this.CONFIG.SIDEBAR_TRIGGER + "[data-active-count]")
        if (!toggleBtn) return

        toggleBtn.setAttribute("data-active-count", count)
    }

    removeActiveTag(category) {
        // Uncheck the matching input
        const input = this.block.querySelector(`${this.CONFIG.FILTER_OPTION}[value="${category}"]`)

        if (input) {
            input.checked = false

            // Remove active class from the option
            const filterOption = input.closest(".customer-block__interactive-map__filter-option")
            if (filterOption) {
                filterOption.classList.remove("is-active")
            }
        }

        // Deselect track or POI
        this.stepsManager?.deselectStep(category)
        this.poiManager?.filterByType(category, null, false)

        // Refresh the UI
        this.updateActiveTags()

        // If no step filter remains, return to the global view
        const activeStep = this.stepsManager?.getSelectedStep()
        if (!activeStep) {
            this.stepsManager?.fitGlobalBounds()
        }
    }

    // ==========================================
    // MAP FILTERS
    // ==========================================

    initMapFilters() {
        const mapFilters = this.block.querySelectorAll(this.CONFIG.FILTER_OPTION)
        if (!mapFilters) return

        for (let i = 0; i < mapFilters.length; i++) {
            mapFilters[i].addEventListener("change", this.filterMap.bind(this))
        }
    }

    // ==========================================
    // STEP DESELECTION
    // ==========================================

    /**
     * Wires the two ways of dropping the active step:
     * the card close button and a second click on the checked radio
     */
    initStepDeselection() {
        const closeButtons = this.block.querySelectorAll(this.CONFIG.CLOSE_STEP_CARD)

        for (const button of closeButtons) {
            button.addEventListener("click", (event) => {
                event.preventDefault()
                event.stopPropagation()

                const card = button.closest(this.CONFIG.STEP_CARD)
                this.clearStepSelection(card?.dataset.stepCard)
            })
        }

        const stepInputs = this.block.querySelectorAll(`${this.CONFIG.FILTER_OPTION}[data-filter-key="steps"]`)

        for (const input of stepInputs) {
            // A radio that is already checked never fires `change`, so the on/off toggle lives on `click`
            input.addEventListener("click", () => {
                if (this.stepsManager?.getSelectedStep()?.id !== input.value) return

                this.clearStepSelection(input.value)
            })
        }
    }

    /**
     * Drops the active step and restores the global view
     */
    clearStepSelection(stepId) {
        if (!stepId) return

        const input = this.block.querySelector(
            `${this.CONFIG.FILTER_OPTION}[data-filter-key="steps"][value="${stepId}"]`
        )

        if (input) {
            input.checked = false
            this.setOptionActive(input.closest(".customer-block__interactive-map__filter-option"), false)
        }

        this.stepsManager?.deselectStep(stepId)
        this.updateActiveTags()
        this.stepsManager?.fitGlobalBounds()
    }

    async filterMap(event) {
        const input = event.target
        const key = input.dataset.filterKey
        const optionEl = input.closest(".customer-block__interactive-map__filter-option")
        const closestParent = input.closest("[data-json]")
        const data = closestParent ? JSON.parse(closestParent.dataset.json) : null

        if (key === "steps") {
            // The radio was just toggled off, nothing left to select
            if (!input.checked) return

            if (!data || !data.gpx?.options?.id) {
                console.warn("No step ID provided")
                return
            }

            const stepId = data.gpx.options.id
            this.stepsManager?.selectStep(stepId)
            // Active visual state for the step option
            if (optionEl) {
                this.setOptionActive(optionEl, true)
            }
        } else {
            // POI filter (checkbox)
            const poiSource = input.dataset.source
            const poiCategory = input.value
            const isChecked = input.checked

            // Show loading state while fetching
            if (optionEl) {
                this.setOptionLoading(optionEl, true)
                input.disabled = true
            }

            try {
                await this.poiManager?.filterByType(poiCategory, poiSource, isChecked)
                if (optionEl) {
                    this.setOptionActive(optionEl, !!isChecked)
                }
            } catch (e) {
                console.error("FiltersManager: error while filtering POIs", e)
                // On error, reset the input when needed
                if (isChecked) {
                    input.checked = false
                }
            } finally {
                if (optionEl) {
                    this.setOptionLoading(optionEl, false)
                    input.disabled = false
                }
            }
        }
    }

    /**
     * Toggles loading state on a filter option
     */
    setOptionLoading(optionEl, isLoading) {
        if (!optionEl) return
        optionEl.classList.toggle("is-loading", !!isLoading)
        optionEl.setAttribute("aria-busy", isLoading ? "true" : "false")

        let loader = optionEl.querySelector(".customer-block__interactive-map__filter-option__loader")
        if (isLoading) {
            if (!loader) {
                loader = document.createElement("span")
                loader.className = "customer-block__interactive-map__filter-option__loader"
                loader.setAttribute("aria-hidden", "true")
                optionEl.appendChild(loader)
            }
        } else if (loader) {
            loader.remove()
        }
    }

    /**
     * Toggles the visual active state on a filter option
     */
    setOptionActive(optionEl, isActive) {
        if (!optionEl) return
        optionEl.classList.toggle("is-active", !!isActive)
    }
}
